import BaseTableNew from 'components/atoms/base-table/new';
import React, { useEffect, type FC } from 'react';
import BaseButton from 'components/atoms/baseButton';
import PersonWorkspaceIcon from 'assets/img/person-workspace.svg?react';
import {
  useNewlyTableDataQueryFilter,
  useConsoleTable,
  useGetNewlyTableData,
  CCM_CONSOLE_PAGE_SIZE,
} from 'infrastructure/hooks/ccm/console';
import { useTranslation } from 'react-i18next';
import { checkIsPageAvailable } from 'infrastructure/hooks/ccm/console/utils';

import CcmConsoleTableBlock from '../table-block';
import { useColumns } from './columns';
import s from './styles.module.scss';
import { AccountsSelect } from '../select-boxes/select-account';
import { NumberOfCallSelectBox } from '../select-boxes/number-of-call';

import type {
  INewlyTableData,
  INewlyTableDataFilters,
} from 'infrastructure/interfaces';

interface NewlyEnrolledTableProps {}

const NewlyEnrolledTable: FC<NewlyEnrolledTableProps> = () => {
  const { filter, updateFilter } = useNewlyTableDataQueryFilter();
  const { data, isLoading } = useGetNewlyTableData(filter);
  const columns = useColumns({ filter });
  const { t } = useTranslation();
  const items = filter['n-allExcluded'] ? [] : data?.data || [];
  const total = filter['n-allExcluded'] ? 0 : data?.count || 0;

  const { onSort, pushToQueue, rowSelection, disableAddToQueue } =
    useConsoleTable<INewlyTableDataFilters, INewlyTableData>({
      filter,
      updateFilter,
      tablePrefix: 'n-',
      excludeSortFields: [
        'n-page',
        'n-allExcluded',
        'n-clinicGuid',
        'n-excludeClinicGuid',
        'patientGuid',
      ],
      tableName: 'newly',
    });

  useEffect(() => {
    const isPageExist = checkIsPageAvailable(
      filter['n-page'],
      total,
      items.length,
    );
    if (!isPageExist && !isLoading && filter['n-page'] !== 1) {
      updateFilter({ 'n-page': undefined });
    }
  }, [filter['n-page'], total, isLoading, items.length]);

  return (
    <CcmConsoleTableBlock
      title={t('labels.newlyEnrolledPatients')}
      total={total}
    >
      <div className={s['select-wrapper']}>
        <AccountsSelect
          updateFilter={updateFilter}
          tablePrefix="n-"
          excludeClinicGuid={filter['n-excludeClinicGuid']}
          clinicGuid={filter['n-clinicGuid']}
          isAllExcluded={filter['n-allExcluded']}
        />
        <NumberOfCallSelectBox
          tablePrefix="n-"
          cyPrefix="follow-up"
          value={filter['n-numberOfCalls']}
          updateFilter={updateFilter}
        />
        <BaseButton
          label={t('labels.addToWorkQueue')}
          className={s['add-queue-btn']}
          icon={<PersonWorkspaceIcon />}
          disabled={disableAddToQueue}
          onClick={pushToQueue}
        />
      </div>
      <BaseTableNew
        items={items}
        columns={columns}
        bordered
        loading={isLoading}
        pagination={{
          current: filter['n-page'],
          pageSize: CCM_CONSOLE_PAGE_SIZE,
          total,
          position: ['bottomCenter'],
        }}
        onChangePage={(newPage) => {
          updateFilter({ 'n-page': newPage });
        }}
        onSort={onSort}
        dataCy="newly-enrolled-patients-table"
        tableRowSelection={rowSelection}
        className={s.table}
        rowHoverable={false}
      />
    </CcmConsoleTableBlock>
  );
};

export default NewlyEnrolledTable;
